import React, { useEffect, useMemo, useRef } from "react";
import { Animated, StyleSheet, View } from "react-native";

type Props = {
  width: number;
};

const ImageLoadingAnimation = React.memo(function ImageLoadingAnimation({
  width,
}: Props): React.JSX.Element {
  const shimmerAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const shimmerLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnimation, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnimation, {
          toValue: 0,
          duration: 2000,
          useNativeDriver: true,
        }),
      ]),
    );
    shimmerLoop.start();
    return () => shimmerLoop.stop();
  }, [shimmerAnimation]);

  const shimmerTranslate = useMemo(
    () =>
      shimmerAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [-width, width],
      }),
    [shimmerAnimation, width],
  );

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.shimmer,
          {
            transform: [{ translateX: shimmerTranslate }],
          },
        ]}
      />
      <View style={styles.content}>
        <View style={styles.linesContainer}>
          <View style={[styles.line, { width: "90%" }]} />
          <View style={[styles.line, { width: "60%" }]} />
          <View style={[styles.line, { width: "30%" }]} />
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "#E0E0E0",
    justifyContent: "space-between",
    padding: 16,
    zIndex: 10, // Ensure placeholder is above the image
  },
  shimmer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
  content: {
    flex: 1,
    justifyContent: "space-between",
  },
  linesContainer: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  line: {
    height: 12,
    borderRadius: 6,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    marginBottom: 8,
  },
});

export default ImageLoadingAnimation;
